import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventAreaApi, campusApi } from '../services/api';
import { PageHead, Card, Btn, FormGroup, Input, Select, Textarea, Spinner, Toast, Modal, Empty } from '../components/UI';

export default function EventAreasPage() {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null); // null | "create" | area object
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    campusID: '',
    areaName: '',
    areaCode: '',
    capacity: '',
    description: '',
    requiresApproval: true,
    isActive: true
  });

  useEffect(() => {
    campusApi.getAll()
      .then(r => setCampuses(r.data.data || []))
      .catch(() => setCampuses([{ campusID: 1, campusName: 'Chinchwad' }, { campusID: 2, campusName: 'Hinjawadi' }]));
  }, []);

  useEffect(() => { load(); }, [campusId]);

  async function load() {
    setLoading(true);
    try {
      const res = await eventAreaApi.getAll(campusId || undefined, true);
      setAreas(res.data.data || []);
    } catch {
      setAreas(DEMO.filter(a => !campusId || a.campusID === parseInt(campusId, 10)));
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setForm({
      campusID: campuses[0]?.campusID || '',
      areaName: '',
      areaCode: '',
      capacity: '',
      description: '',
      requiresApproval: true,
      isActive: true
    });
    setModal('create');
  }

  function openEdit(area) {
    setForm({
      campusID: area.campusID,
      areaName: area.areaName,
      areaCode: area.areaCode || '',
      capacity: area.capacity || '',
      description: area.description || '',
      requiresApproval: area.requiresApproval !== false,
      isActive: area.isActive
    });
    setModal(area);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        campusID: parseInt(form.campusID, 10),
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        requiresApproval: !!form.requiresApproval
      };

      if (modal === 'create') {
        await eventAreaApi.create(payload);
      } else {
        await eventAreaApi.update(modal.eventAreaID, payload);
      }

      setToast({ msg: `Event area ${modal === 'create' ? 'created' : 'updated'} successfully.`, type: 'success' });
      setModal(null);
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Save failed.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(area) {
    try {
      if (area.isActive) await eventAreaApi.deactivate(area.eventAreaID);
      else await eventAreaApi.activate(area.eventAreaID);
      setToast({ msg: `Area ${area.isActive ? 'deactivated' : 'activated'}.`, type: 'success' });
      load();
    } catch {
      setToast({ msg: 'Failed to update status.', type: 'error' });
    }
  }

  return (
    <div className="page-anim">
      <PageHead
        title="Event Areas"
        subtitle="Manage booking LEs across campuses"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={campusId}
              onChange={e => setCampusId(e.target.value)}
              style={{ padding: '7px 12px', fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', outline: 'none', background: 'white' }}
            >
              <option value="">All Campuses</option>
              {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
            </select>
            <Btn variant="primary" onClick={openCreate}>+ Add LE</Btn>
          </div>
        }
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem', gap: 10 }}>
          <Spinner size={24} /><span style={{ color: 'var(--text2)' }}>Loading...</span>
        </div>
      ) : areas.length === 0 ? <Empty icon="LE" text="No booking LEs found" /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: '1.2rem' }}>
          {areas.map(a => (
            <Card
              key={a.eventAreaID}
              style={{ transition: 'transform .15s,box-shadow .15s' }}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                  LE
                </div>
                <span style={{ background: a.isActive ? 'var(--green-light)' : 'var(--bg2)', color: a.isActive ? 'var(--green)' : 'var(--text3)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>
                  {a.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 15, marginBottom: 3 }}>{a.areaName}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                {a.campusName}{a.areaCode ? ` - ${a.areaCode}` : ''}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
                Approval: {a.requiresApproval === false ? 'Auto approve (first come first serve)' : 'Management approval required'}
              </div>
              {a.capacity && <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>Capacity: {a.capacity}</div>}
              {a.description && <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>{a.description}</div>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Btn size="sm" variant="ghost" onClick={() => openEdit(a)}>Edit</Btn>
                <Btn size="sm" variant={a.isActive ? 'red' : 'green'} onClick={() => toggleActive(a)}>
                  {a.isActive ? 'Deactivate' : 'Activate'}
                </Btn>
                <Btn size="sm" variant="outline" onClick={() => navigate(`/new-booking?areaId=${a.eventAreaID}`)}>Booking LE</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal === 'create' ? 'Add Booking LE' : 'Edit Booking LE'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <FormGroup label="Campus *" full>
                <Select value={form.campusID} onChange={e => setForm(f => ({ ...f, campusID: e.target.value }))} required>
                  <option value="">Select Campus</option>
                  {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
                </Select>
              </FormGroup>
              <FormGroup label="LE Name *">
                <Input value={form.areaName} onChange={e => setForm(f => ({ ...f, areaName: e.target.value }))} placeholder="e.g. Main MPH" required />
              </FormGroup>
              <FormGroup label="LE Code">
                <Input value={form.areaCode} onChange={e => setForm(f => ({ ...f, areaCode: e.target.value }))} placeholder="e.g. MPH" />
              </FormGroup>
              <FormGroup label="Capacity">
                <Input type="number" min={1} value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} placeholder="Optional" />
              </FormGroup>
              <FormGroup label="Description" full>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description..." speechToText style={{ minHeight: 130 }} />
              </FormGroup>
              <FormGroup label="Approval Flow" full>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.requiresApproval} onChange={e => setForm(f => ({ ...f, requiresApproval: e.target.checked }))} />
                  Require management approval
                </label>
              </FormGroup>
              {modal !== 'create' && (
                <FormGroup label="Status" full>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                    Active
                  </label>
                </FormGroup>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn type="submit" variant="primary" disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
                {saving ? <Spinner size={16} color="white" /> : modal === 'create' ? 'Create LE' : 'Save Changes'}
              </Btn>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            </div>
          </form>
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const DEMO = [
  { eventAreaID: 1, campusID: 1, campusName: 'Chinchwad', areaName: 'Main MPH', areaCode: 'MPH', capacity: 500, requiresApproval: true, isActive: true },
  { eventAreaID: 2, campusID: 1, campusName: 'Chinchwad', areaName: 'Classrooms', areaCode: 'CLS', capacity: 30, requiresApproval: false, isActive: true },
  { eventAreaID: 3, campusID: 2, campusName: 'Hinjawadi', areaName: 'Auditorium', areaCode: 'AUD', capacity: 300, requiresApproval: true, isActive: true }
];
